import React from 'react';
import { useQuery } from 'react-query';
import api from '../services/api';

/**
 * Renders form inputs for the given entity's custom field definitions.
 * Values map is { [key]: value }. onChange(values) receives the new map.
 */
const CustomFieldsInput = ({ entity, values = {}, onChange }) => {
  const { data: defs = [] } = useQuery(
    ['custom-fields', entity],
    () => api.get('/custom-fields', { params: { entity } }).then((r) => r.data),
    { staleTime: 60 * 1000 }
  );

  if (defs.length === 0) return null;

  const setField = (key, val) => {
    onChange({ ...values, [key]: val });
  };

  return (
    <div className="space-y-3">
      {defs.map((def) => {
        const v = values[def.key] ?? '';
        const label = (
          <label className="block text-sm font-medium text-gray-700">
            {def.label}{def.required && <span className="text-red-500 ml-0.5">*</span>}
          </label>
        );

        switch (def.type) {
          case 'textarea':
            return (
              <div key={def._id}>
                {label}
                <textarea
                  value={v}
                  onChange={(e) => setField(def.key, e.target.value)}
                  rows={3}
                  className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            );
          case 'number':
            return (
              <div key={def._id}>
                {label}
                <input
                  type="number"
                  value={v}
                  onChange={(e) => setField(def.key, e.target.value)}
                  className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            );
          case 'date':
            return (
              <div key={def._id}>
                {label}
                <input
                  type="date"
                  value={v ? (typeof v === 'string' ? v.slice(0, 10) : new Date(v).toISOString().slice(0, 10)) : ''}
                  onChange={(e) => setField(def.key, e.target.value)}
                  className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            );
          case 'dropdown':
            return (
              <div key={def._id}>
                {label}
                <select
                  value={v}
                  onChange={(e) => setField(def.key, e.target.value)}
                  className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">— Select —</option>
                  {def.options.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>
            );
          case 'checkbox':
            return (
              <label key={def._id} className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={!!v}
                  onChange={(e) => setField(def.key, e.target.checked)}
                />
                {def.label}{def.required && <span className="text-red-500">*</span>}
              </label>
            );
          default:
            return (
              <div key={def._id}>
                {label}
                <input
                  type="text"
                  value={v}
                  onChange={(e) => setField(def.key, e.target.value)}
                  className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            );
        }
      })}
    </div>
  );
};

export default CustomFieldsInput;
